import { CandidatLink } from '../../components/candidat/CandidatLink';
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Users, CalendarCheck, Percent, BookOpen, ClipboardCheck, UserX } from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis,
  Tooltip, Legend, CartesianGrid,
} from 'recharts';
import { KpiCard, pct, fmtDateShort, LoadingCard, EmptyCard } from './shared';

export const GroupeTab = ({ data, loading, onSelectCandidat }) => {
  if (loading) return <LoadingCard />;
  if (!data) return <EmptyCard message="Sélectionnez une session pour afficher le rapport" />;

  const timeline = (data.timeline || []).map(t => ({ ...t, dateShort: fmtDateShort(t.date) }));
  const presenceData = timeline.filter(t => t.presents + t.retards + t.absents > 0);
  const qcmData = timeline.filter(t => t.qcm_moyenne !== null && t.qcm_moyenne !== undefined);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="group-kpis">
        <KpiCard icon={Users} label="Effectif" value={data.kpis.effectif} sub={`${data.kpis.actifs} actif(s)${data.kpis.en_difficulte ? ` · ${data.kpis.en_difficulte} en difficulté` : ''}`} testId="kpi-effectif" />
        <KpiCard icon={CalendarCheck} label="Séances réalisées" value={`${data.kpis.seances_realisees}/${data.kpis.seances_prevues}`} color="emerald" testId="kpi-seances" />
        <KpiCard icon={Percent} label="Présence moyenne" value={pct(data.kpis.taux_presence_moyen)} sub={`${data.kpis.heures_absence_total} h d'absence cumulées`} color="sky" testId="kpi-presence" />
        <KpiCard icon={ClipboardCheck} label="Moyenne QCM" value={pct(data.kpis.moyenne_qcm)} sub={`Comp. A/ECA/NA : ${data.kpis.competences.A}/${data.kpis.competences.ECA}/${data.kpis.competences.NA}`} color="violet" testId="kpi-qcm" />
        <KpiCard icon={BookOpen} label="Avancement programme" value={pct(data.kpis.progression_pct)} sub={`${data.kpis.modules_realises}/${data.kpis.total_modules} modules`} color="amber" testId="kpi-progression" />
        <KpiCard icon={UserX} label="Abandons" value={data.kpis.abandons} color="red" testId="kpi-abandons" />
      </div>

      {(presenceData.length > 0 || qcmData.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {presenceData.length > 0 && (
            <Card className="border-0 shadow-sm" data-testid="chart-assiduite">
              <CardHeader className="pb-2"><CardTitle className="text-base">Assiduité par séance</CardTitle></CardHeader>
              <CardContent className="h-64">
                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={200}>
                  <BarChart data={presenceData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="dateShort" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="presents" name="Présents" stackId="a" fill="#10b981" />
                    <Bar dataKey="retards" name="Retards" stackId="a" fill="#f59e0b" />
                    <Bar dataKey="absents" name="Absents" stackId="a" fill="#ef4444" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
          {qcmData.length > 0 && (
            <Card className="border-0 shadow-sm" data-testid="chart-scores">
              <CardHeader className="pb-2"><CardTitle className="text-base">Évolution de la moyenne QCM</CardTitle></CardHeader>
              <CardContent className="h-64">
                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={200}>
                  <LineChart data={qcmData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="dateShort" tick={{ fontSize: 11 }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => [`${v}%`, 'Moyenne QCM']} />
                    <Line type="monotone" dataKey="qcm_moyenne" name="Moyenne QCM" stroke="#7c3aed" strokeWidth={2} dot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      <Card className="border-0 shadow-sm overflow-hidden">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Détail par apprenant — {data.dossier.code} {data.dossier.intitule}</CardTitle>
        </CardHeader>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="group-table">
            <thead>
              <tr className="bg-slate-100 text-xs text-slate-500 uppercase tracking-wide">
                <th className="text-left px-4 py-2.5">Apprenant</th>
                <th className="text-center px-2 py-2.5">Présents</th>
                <th className="text-center px-2 py-2.5">Retards</th>
                <th className="text-center px-2 py-2.5">Absents</th>
                <th className="text-center px-2 py-2.5">Taux présence</th>
                <th className="text-center px-2 py-2.5">Heures</th>
                <th className="text-center px-2 py-2.5">QCM</th>
                <th className="text-center px-2 py-2.5">Moy. QCM</th>
                <th className="text-center px-2 py-2.5">Évals</th>
                <th className="text-center px-2 py-2.5">Moy. évals</th>
                <th className="text-center px-2 py-2.5" title="Savoir-faire et connaissances : Acquis / En cours / Non acquis">A / ECA / NA</th>
                <th className="text-center px-2 py-2.5">Obs.</th>
                <th className="text-center px-2 py-2.5">Statut</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {data.candidats.map(r => (
                <tr
                  key={r.candidat_id}
                  className="hover:bg-slate-50 cursor-pointer transition-colors"
                  data-testid={`group-row-${r.candidat_id}`}
                  onClick={() => onSelectCandidat(r.candidat_id)}
                >
                  <td className="px-4 py-2.5 font-medium text-slate-900"><CandidatLink dossierId={data.dossier?.id} candidatId={r.candidat_id}>{r.nom} {r.prenom}</CandidatLink></td>
                  <td className="text-center text-emerald-600">{r.presents}</td>
                  <td className="text-center text-amber-600">{r.retards}</td>
                  <td className="text-center text-red-600">{r.absents}</td>
                  <td className="text-center font-medium">{pct(r.taux_presence)}</td>
                  <td className="text-center text-slate-500" title={`${r.heures_absence} h d'absence`}>{r.heures_realisees} h</td>
                  <td className="text-center text-slate-500">{r.qcm_nb}</td>
                  <td className="text-center font-medium">{pct(r.qcm_moyenne)}</td>
                  <td className="text-center text-slate-500">{r.evals_nb}</td>
                  <td className="text-center font-medium">{pct(r.evals_moyenne)}</td>
                  <td className="text-center text-xs"><span className="text-emerald-600">{r.competences.A}</span> / <span className="text-amber-600">{r.competences.ECA}</span> / <span className="text-red-600">{r.competences.NA}</span></td>
                  <td className="text-center text-slate-500">{r.observations}</td>
                  <td className="text-center">
                    {r.abandon
                      ? <Badge className="bg-red-100 text-red-700 border-0" title={r.abandon.motif}>Abandon {fmtDateShort(r.abandon.date)}</Badge>
                      : r.en_difficulte ? <Badge className="bg-amber-100 text-amber-700 border-0">Difficulté</Badge>
                      : <Badge className="bg-emerald-100 text-emerald-700 border-0">Actif</Badge>}
                  </td>
                </tr>
              ))}
              {data.candidats.length === 0 && (
                <tr><td colSpan={13} className="text-center py-8 text-slate-400">Aucun candidat dans cette session</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};
